import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from './ui/icon'
import { mdiAccountKeyOutline, mdiAccountPlusOutline, mdiTrashCanOutline } from '@mdi/js'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from './ui/alert-dialog'
import { Can, useCan } from '../contexts/PermissionsContext'
import { useAssetAccess, useRevokeAccessGrant, type AccessGrant } from '../api/accessGrants'
import { EmptyState } from './ui/empty-state'
import { grantOriginLabel } from '../lib/grant-origin'

/**
 * "Who has access here, and from where" (spec 2026-09-04-03, frontend change 3).
 *
 * The panel lists the grants anchored on this asset AND the ones inherited from
 * its ancestors, because that inheritance is exactly what a manager needs to
 * see before wondering why a resident of flat 12 can read the site's boiler
 * room. An inherited grant is labelled with where it comes from and cannot be
 * revoked from here — you revoke it where it was granted.
 */
export function AssetAccessPanel({
    org,
    assetUid,
    assetName,
    onInvite,
}: {
    org: string
    assetUid: string
    assetName?: string
    /** Opens the invite dialog pre-filled with this asset. */
    onInvite: () => void
}) {
    const { t, i18n } = useTranslation('access')
    const canRead = useCan('members.read')
    const { data, isLoading } = useAssetAccess(org, assetUid, canRead)
    const revoke = useRevokeAccessGrant(org)
    const [pendingRevoke, setPendingRevoke] = useState<AccessGrant | null>(null)

    if (!canRead) return null

    const grants = (data?.data ?? []) as AccessGrant[]

    return (
        <Card data-testid="asset-access-panel">
            <CardHeader className="flex flex-row items-start justify-between gap-2">
                <div>
                    <CardTitle className="flex items-center gap-2">
                        <AppIcon path={mdiAccountKeyOutline} size="md" />
                        {t('panel.title')}
                    </CardTitle>
                    <CardDescription>{t('panel.description')}</CardDescription>
                </div>
                <Can perm="members.write">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onInvite}
                        aria-label={t('panel.invite')}
                        title={t('panel.invite')}
                        data-testid="asset-access-invite-btn"
                    >
                        <AppIcon path={mdiAccountPlusOutline} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('panel.invite')}</span>
                    </Button>
                </Can>
            </CardHeader>
            <CardContent>
                {isLoading && (
                    <p className="text-sm text-muted-foreground">{t('panel.loading')}</p>
                )}
                {!isLoading && grants.length === 0 && (
                    <EmptyState
                        variant="inline"
                        icon={mdiAccountKeyOutline}
                        title={t('panel.empty')}
                        data-testid="asset-access-empty"
                    />
                )}
                <ul className="divide-y">
                    {grants.map((grant) => (
                        <li
                            key={grant.uid}
                            className="flex items-center justify-between gap-3 py-2"
                            data-testid="asset-access-row"
                        >
                            <div className="min-w-0">
                                <p className="truncate text-sm font-medium">
                                    {grant.user?.name || grant.user?.email}
                                </p>
                                <p className="truncate text-xs text-muted-foreground">
                                    {grant.inherited
                                        ? t('panel.inherited', { asset: grant.asset?.name ?? '' })
                                        : t('panel.granted_here')}
                                    {` · ${grantOriginLabel(grant, t, i18n.language)}`}
                                    {grant.valid_until
                                        ? ` · ${t('panel.until', {
                                              date: new Date(grant.valid_until).toLocaleDateString(
                                                  i18n.language
                                              ),
                                          })}`
                                        : ''}
                                </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                                <Badge variant="secondary">{grant.role?.code}</Badge>
                                {!grant.inherited && grant.source !== 'derived' && (
                                    <Can perm="members.write">
                                        <Button
                                            variant="destructive"
                                            size="icon"
                                            aria-label={t('panel.revoke')}
                                            title={t('panel.revoke')}
                                            data-testid="asset-access-revoke-btn"
                                            onClick={() => setPendingRevoke(grant)}
                                        >
                                            <AppIcon path={mdiTrashCanOutline} size="sm" />
                                        </Button>
                                    </Can>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            </CardContent>

            <AlertDialog
                open={Boolean(pendingRevoke)}
                onOpenChange={(open) => !open && setPendingRevoke(null)}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('panel.revoke_confirm_title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('panel.revoke_confirm_description', {
                                name:
                                    pendingRevoke?.user?.name ||
                                    pendingRevoke?.user?.email ||
                                    '',
                                asset: assetName ?? pendingRevoke?.asset?.name ?? '',
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="asset-access-revoke-cancel">
                            {t('invite.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="asset-access-revoke-confirm"
                            onClick={() => {
                                if (!pendingRevoke) return
                                revoke.mutate({
                                    params: {
                                        path: { orgCode: org, grantUid: pendingRevoke.uid },
                                    },
                                })
                                setPendingRevoke(null)
                            }}
                        >
                            {t('panel.revoke')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    )
}
